import type { CompetenceRecord } from "../../api/client";
import { Callout } from "../../components/Callout";
import { SelectField } from "../../components/SelectField";
import { TextField } from "../../components/TextField";

/** Screen 12's select, in the words Cl. 7.3 needs it in. */
const ENGAGEMENTS = ["Employee", "Contracted external auditor"] as const;

/**
 * Screen 12 — identity and engagement.
 *
 * The callout is not decoration: it is the only place the auditor is told who invited them
 * and what that body proposed them for. Both come from the server, because the browser
 * cannot know either.
 *
 * Email is `type="text"` with `inputmode="email"`. `type="email"` returns null from
 * `selectionStart` and throws on `setSelectionRange`, so a controlled field cannot restore
 * the caret and text appears to type backwards — data dictionary §9.
 */
export function IdentityStep({
  record,
  set,
  timezones,
  invitedBy,
  proposedStandards,
  errorFor,
}: {
  record: CompetenceRecord;
  set: (patch: Partial<CompetenceRecord>) => void;
  timezones: readonly string[];
  invitedBy: string | null;
  proposedStandards: readonly string[];
  errorFor: (field: string) => string | undefined;
}) {
  return (
    <div className="card">
      <h2>Identity and engagement</h2>

      {invitedBy ? (
        <Callout title={`You were invited by ${invitedBy}.`}>
          {proposedStandards.length > 0
            ? `They proposed you for ${proposedStandards.join(" and ")}. `
            : ""}
          Complete your own competence record — the certification body then verifies it.
        </Callout>
      ) : null}

      <div className="grid2">
        <TextField
          id="auditor-name"
          label="Full name"
          value={record.name ?? ""}
          onChange={(name) => set({ name })}
          error={errorFor("name")}
          required
        />
        <TextField
          id="auditor-email"
          label="Email"
          kind="email"
          hint="Your sign-in address and where notifications go"
          value={record.email ?? ""}
          onChange={(email) => set({ email })}
          error={errorFor("email")}
          required
        />
      </div>

      <div className="grid3">
        <SelectField
          id="auditor-engagement"
          label="Engagement"
          hint="ISO/IEC 17021-1 Cl. 7.3"
          value={record.engagement ?? ""}
          onChange={(engagement) => set({ engagement })}
          options={ENGAGEMENTS.map((value) => ({ value, label: value }))}
          error={errorFor("engagement")}
          required
        />
        <TextField
          id="auditor-position"
          label="Position"
          placeholder="e.g. Lead auditor"
          value={record.position ?? ""}
          onChange={(position) => set({ position })}
          error={errorFor("position")}
          required
        />
        <SelectField
          id="auditor-timezone"
          label="Time zone"
          hint="Governs your audit day availability"
          value={record.timezone ?? ""}
          onChange={(timezone) => set({ timezone })}
          options={timezones.map((value) => ({ value, label: value }))}
          error={errorFor("timezone")}
          required
        />
      </div>
    </div>
  );
}
